









































import { Component, Prop, Vue } from 'vue-property-decorator';
import KeycloakStore from '@/stores/user/keycloak.store';
import { getModule } from 'vuex-module-decorators';
import { AddressDto, BlockDto, RegisterFragmentDto, UserRegistrationDto } from '@/api';
import UserStore from '@/stores/user/user.store';
import KeycloakUserRepresentation from '@/util/KeycloakUserRepresentation';
import AddressEdit from '@/views/account/user/AddressEdit.vue';
import { ErrorStore } from '@/stores/error.store';
import { StandardErrors } from '@/util/StandardErrors';
import ErrorKey from '@/util/ErrorKey';
import { Addresses } from '@/util/Addresses';
import AddressField from '@/views/account/user/AddressField.vue';
import LoadingIndicator from '@/views/common/LoadingIndicator.vue';
import { ShopContext } from '@/util/ShopContext';
import StyledRadio from '@/views/common/StyledRadio.vue';
import Placeholder from '@/views/account/Placeholder.vue';
import RegisterMegacardForm from '@/views/account/RegisterMegacardSelector.vue';
import MegacardStore from '@/stores/user/megacard.store';
import { MegacardChoice, MegacardChoiceEvent } from '@/util/megacard-connector-models';
import Routes from '@/util/Routes';
import { CmsStore } from '@/stores/cms.store';
import BlocksDispatcher from '@/views/cms/BlocksDispatcher.vue';

@Component({
  components: {RegisterMegacardForm, Placeholder, StyledRadio,
    LoadingIndicator, AddressField, AddressEdit, BlocksDispatcher},
})
export default class Register extends Vue {
  private keycloakStore = getModule(KeycloakStore, this.$store);
  private megacardStore = getModule(MegacardStore, this.$store);
  private cmsStore = getModule(CmsStore, this.$store);
  private userStore = getModule(UserStore, this.$store);
  private errorStore = getModule(ErrorStore, this.$store);

  @Prop({required: false, default: false})
  public readonly fromCart!: boolean;

  public missingInBackend = false;

  private loading = true;
  private submitting = false;
  private birthDate: string = '';
  private address: AddressDto = Addresses.emptyAddress();
  private megacardChoice: MegacardChoice | null = null;
  private megacardError = true;
  private registerFragment: RegisterFragmentDto | null = null;

  get keycloakUser(): KeycloakUserRepresentation {
    return this.keycloakStore.currentUser;
  }

  private get authenticated(): boolean {
    return this.keycloakStore.authenticated;
  }

  private get email(): string {
    return this.keycloakUser.email;
  }

  private get id(): string {
    return this.keycloakUser.id;
  }

  private get sidebar(): BlockDto[] | null {
    return this.registerFragment?.sidebar ?? null;
  }

  public createDefaultAddress(): AddressDto {
    return Addresses.emptyAddress();
  }

  public async mounted(): Promise<void> {
    await this.keycloakStore.initialize();
    const user = await this.userStore.fetchUserOnce();
    if (!user.success) {
      this.errorStore.dismissResponseError(user);
    }
    if (!this.authenticated || user.success) {
      await this.$router.push('/');
    } else {
      this.missingInBackend = user.error?.errorKey === ErrorKey.NoSuchUserException;
      this.address = this.createDefaultAddress();
      this.loading = false;
    }
    if (!this.megacardChoice) {
      this.megacardChoice = MegacardChoice.WANT;
    }
    const cmsFragment = await this.cmsStore.fetchRegisterFragment(this.userStore.currentLanguage);
    if (cmsFragment.success) {
      this.registerFragment = cmsFragment.payload ?? null;
    }
  }

  public logout(): void {
    this.keycloakStore.logout();
  }

  public async save(): Promise<void> {
    if (!this.validate()) {
      return;
    }
    this.submitting = true;
    try {
      const response = await this.userStore.createUser(this.mapStateToRequest());
      if (response.success) {
        await this.$router.push({
          name: Routes.RegisterConfirmation,
          query: {fromCart: this.fromCart.toString()},
        });
      }
    } finally {
      this.submitting = false;
    }
  }

  private mapStateToRequest(): UserRegistrationDto {
    return {
      model: {
        salutation: this.address.salutation, userId: this.id,
        firstname: this.address.firstName, lastname: this.address.lastName,
        email: this.email, assignedShopCode: ShopContext.shopCode,
        birthDate: this.birthDate, billingAddress: this.address,
        company: this.address.company,
        megacard: this.megacardStore.requestedMegacard ?? undefined,
      },
      wantsMegacard: this.megacardChoice === MegacardChoice.WANT,
    };
  }

  public onMegacardChange(event: MegacardChoiceEvent): void {
    this.megacardChoice = event.choice;
    if (event.address) {
      Object.assign(
        this.address,
        event.address,
        {id: null},
      );
      this.megacardError = false;
    } else {
      switch (this.megacardChoice) {
        case null:
        case MegacardChoice.HAVE:
          this.megacardError = true;
          break;
        default:
          this.megacardError = false;
      }
    }
  }

  private validate(): boolean {
    let valid = !this.megacardError;
    if (!Addresses.checkAddress(this.address)) {
      this.errorStore.setError(StandardErrors.of(ErrorKey.WrongAddress));
      valid = false;
    }
    return valid;
  }
}
